import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { Observable, pipe, UnaryFunction } from 'rxjs';
import { tap } from 'rxjs/operators';
import { readFirst } from '@dg/shared-rxjs';
import { deleteUndefinedOrNull } from '@app/shared/utils';

import {
  stateToUrlParams,
  urlParamsToState,
  coerceUrlParams,
  verifyUrlParams,
} from './utils';
import {
  AssignmentsViewModel,
  AssignmentsQueryUrlParams,
  AssignmentsState,
} from '@app/profile/components/profile-assignments/profile-assignments-reactive-store/profile-assignments.model';
import { ProfileAssignmentsFacade } from '@app/profile/components/profile-assignments/profile-assignments-reactive-store/profile-assignments.facade';

/**
 * The UrlSync for assignments enables bi-directional synchronization between
 * url query params and assignments 'state'.
 *
 * The UrlSync class decouples
 *   - UI components from URL synchronization details
 *   - AssignmentsFacade from Router details
 */
@Injectable()
export class AssignmentsBookmarkService {
  /**
   * Create a 'rxjs' operator to tap AssignmentsViewModel emissions and update the
   * url query params.
   */
  public updateUrl: UnaryFunction<
    Observable<AssignmentsViewModel>,
    Observable<AssignmentsViewModel>
  >;

  constructor(
    private facade: ProfileAssignmentsFacade,
    private router: Router,
    private route: ActivatedRoute
  ) {
    // Build operator that taps an AssignmentsViewModel stream
    this.updateUrl = pipe(tap(this.updateUrlFrom.bind(this)));
  }

  /**
   * Initial URL -> Assignments State
   *
   * This is a 'bookmark' feature that will use the current url query params and
   * update the AssignmentsState to match.
   *
   * Note: This only runs once when the assignments results component first inits.
   * We fall back to the Assignments State initial values if there are
   * no overrides in the URL
   */
  public updateState() {
    verifyUrlParams(this.route.snapshot.queryParams);

    const params = coerceUrlParams(this.route.snapshot.queryParams);
    const state = urlParamsToState(deleteUndefinedOrNull(params));

    // We're initializing so reset store
    const resetStore = true;

    const searchAssignments$ = this.facade.searchAssignments(
      { ...state },
      resetStore
    );

    readFirst(searchAssignments$);
  }

  /**
   * Assignments State updates -> URL
   *
   * Update the url with the given state changes
   */
  private updateUrlFrom(state: AssignmentsState) {
    const params = this.buildQueryParams(state);

    if (params) {
      verifyUrlParams(params);

      this.router.navigate([], {
        queryParams: params,
        relativeTo: this.route,
        replaceUrl: true,
      });
    }
  }

  /**
   * Map AssignmentsState to specific parameters we wish to expose/publish on URL
   */
  private buildQueryParams(
    state: AssignmentsState
  ): Partial<AssignmentsQueryUrlParams> {
    const queryParams = stateToUrlParams(state);

    // A pageNum of "0" indicates that state hasn't been fully initialized
    // so wait until it's updated before building...
    if (queryParams.pageNum > 0) {
      return deleteUndefinedOrNull<Partial<AssignmentsQueryUrlParams>>(
        queryParams
      );
    }

    return undefined;
  }
}
